import {LinkButton} from '~/components/LinkButton';
import {HorizontalDivider} from '~/components/HorizontalDivider';
import {useTranslation} from 'react-i18next';
import {SectionShare} from './SectionShare';
import {BlogPageType} from '~/features/blogs/services/GetBlogPageService';
import {Link} from '@remix-run/react';
import {MediaShowcase} from '~/features/shared';
import {Video} from '~/components/Video';
import {twMerge} from 'tailwind-merge';

const IMAGES_CONTAINER_HEIGHT = '450px';
const VIDEO_CONTAINER_HEIGHT = '500px';

const BACKGROUND_HEIGHT = '150px';

export interface SectionContentProps {
  data: BlogPageType;
}

export function SectionContent({data}: SectionContentProps) {
  // console.log(JSON.stringify(data, null, 2));
  const blog = data.page.blog;
  const shareUrl = data.basePage.url;

  const {t} = useTranslation();

  return (
    <div className="flex flex-col xl:flex-row">
      <article className="relative xl:flex-1">
        <div
          className="overflow-hidden absolute top-0 inset-x-5 rounded-xl md:inset-x-10 xl:right-5"
          style={{
            height: IMAGES_CONTAINER_HEIGHT,
          }}
        >
          <MediaShowcase
            media={blog.media}
            height={IMAGES_CONTAINER_HEIGHT}
            showOnlySlider
          />
        </div>

        <div
          className="bg-gray-100 rounded-none lg:rounded-b-3xl xl:rounded-br-none"
          style={{
            height: BACKGROUND_HEIGHT,
          }}
        />

        <div
          style={{
            height: `calc(${IMAGES_CONTAINER_HEIGHT} - ${BACKGROUND_HEIGHT})`,
          }}
        />

        <div className="flex flex-col gap-10 px-5 pt-10 pb-10 md:px-10 xl:pr-5 xl:pb-0">
          <div
            className="rich-text"
            dangerouslySetInnerHTML={{
              __html: blog.contentHtml,
            }}
          />

          <HorizontalDivider className="hidden my-0 xl:block" />

          <SectionShare url={shareUrl} className="hidden xl:flex" />
        </div>
      </article>

      <aside className={`relative xl:shrink-0 xl:w-[400px]`}>
        {blog.video ? (
          <div
            className="overflow-hidden absolute top-0 inset-x-5 bg-black rounded-xl md:inset-x-10 xl:left-5"
            style={{
              height: VIDEO_CONTAINER_HEIGHT,
            }}
          >
            <Video
              className="object-contain w-full h-full"
              video={blog.video}
              controls
              muted
              loop
            />
          </div>
        ) : null}

        <div
          className="hidden bg-gray-100 rounded-b-3xl xl:block xl:rounded-bl-none"
          style={{
            height: BACKGROUND_HEIGHT,
          }}
        />

        {blog.video ? (
          <div
            className="xl:hidden"
            style={{
              height: VIDEO_CONTAINER_HEIGHT,
            }}
          />
        ) : null}

        {blog.video ? (
          <div
            className="hidden xl:block"
            style={{
              height: `calc(${VIDEO_CONTAINER_HEIGHT} - ${BACKGROUND_HEIGHT})`,
            }}
          />
        ) : null}

        <div
          className={twMerge(
            'flex flex-col gap-10 px-5 md:px-10 xl:pl-5 xl:pt-10',
            blog.video && 'pt-10'
          )}
        >
          <div className="flex flex-col p-10 bg-gray-100 rounded-xl">
            <h2 className="mb-6 text-xl font-bold">{t('categorias')}</h2>

            <div className="flex flex-col gap-3">
              {blog.categories.map((category) => (
                <div key={category}>
                  <Link
                    className="text-sm hover:underline text-blue-dark decoration-blue-dark"
                    to={`/blogs/${category}`}
                  >
                    {t(category)}
                  </Link>
                  <hr className="mt-3" />
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col p-10 bg-gray-100 rounded-xl">
            <h2 className="mb-6 text-xl font-bold">
              {t('pages:blog:aside.title')}
            </h2>

            <p className="mb-8 text-sm font-medium">
              {t('pages:blog:aside.content')}
            </p>

            <LinkButton to="?cotiza-tu-proyecto=true">
              {t('pages:blog:aside.action')}
            </LinkButton>
          </div>

          <HorizontalDivider className="my-0 xl:hidden" />

          <SectionShare url={shareUrl} className="xl:hidden" />
        </div>
      </aside>
    </div>
  );
}
